import { useEffect, useState, useCallback } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { Loader2, ArrowLeft, Phone, Mail, MessageCircle, Archive, RotateCcw, Trash2, Printer, Save, FileText, Send, AlertCircle, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";
import { api, API, formatApiErrorDetail } from "../../lib/api";
import { STATUS_OPTIONS } from "./Inquiries";

const OFFER_STATUS = ["", "Angebot wird erstellt", "Angebot versendet", "Angenommen", "Abgelehnt"];
const ORDER_STATUS = ["", "Beauftragt", "Auftrag geplant", "Auftrag durchgeführt", "Abgeschlossen"];

const Field = ({ label, value }) => (
  <div>
    <dt className="text-xs font-semibold uppercase tracking-widest text-mid-gray">{label}</dt>
    <dd className="mt-1 text-sm text-anthracite">{value || "—"}</dd>
  </div>
);

const STATUS_META = {
  gesendet: { color: "bg-green-100 text-green-800 border-green-200", label: "Erfolgreich versendet", Icon: CheckCircle2 },
  fehlgeschlagen: { color: "bg-red-100 text-red-800 border-red-200", label: "Zustellung fehlgeschlagen", Icon: AlertCircle },
  ausstehend: { color: "bg-yellow/20 text-anthracite border-yellow", label: "Versand ausstehend", Icon: Loader2 },
  nicht_erforderlich: { color: "bg-light-gray text-mid-gray border-light-gray", label: "Nicht erforderlich", Icon: Mail },
};

function EmailStatusRow({ label, recipient, status, sentAt, error, attempts, busy, onResend, disabled, testid }) {
  const meta = STATUS_META[status] || STATUS_META.ausstehend;
  const Icon = meta.Icon;
  return (
    <div className="mt-4 space-y-2 rounded-sm border border-light-gray p-3" data-testid={testid}>
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div className="min-w-0">
          <p className="text-sm font-bold text-anthracite">{label}</p>
          <p className="truncate text-xs text-mid-gray">an {recipient}</p>
        </div>
        <span className={`inline-flex items-center gap-1.5 rounded-sm border px-2 py-1 text-xs font-semibold ${meta.color}`}>
          <Icon className="h-3.5 w-3.5" /> {meta.label}
        </span>
      </div>
      <p className="text-xs text-mid-gray">
        Letzter Versand: {sentAt ? new Date(sentAt).toLocaleString("de-DE") : "—"}
        {attempts > 0 && ` · ${attempts} Versuch${attempts > 1 ? "e" : ""}`}
      </p>
      {error && (
        <p className="rounded-sm bg-red-50 p-2 text-xs text-red-700" data-testid={`${testid}-error`}>
          <strong>Fehler:</strong> {error}
        </p>
      )}
      <button
        onClick={onResend}
        disabled={busy || disabled}
        className="inline-flex items-center gap-2 rounded-sm bg-anthracite px-3 py-2 text-xs font-bold text-white hover:bg-brand-black disabled:opacity-50"
        data-testid={`${testid}-resend`}
      >
        {busy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Send className="h-3.5 w-3.5" />}
        E-Mail erneut senden
      </button>
    </div>
  );
}

export default function InquiryDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [inq, setInq] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [note, setNote] = useState("");
  const [form, setForm] = useState({});

  const load = useCallback(async () => {
    try {
      const { data } = await api.get(`/admin/inquiries/${id}`);
      setInq(data);
      setForm({
        status: data.status, assigned_to: data.assigned_to || "",
        callback_date: data.callback_date || "", viewing_date: data.viewing_date || "",
        offer_status: data.offer_status || "", order_status: data.order_status || "",
      });
    } catch {
      toast.error("Anfrage nicht gefunden.");
      navigate("/admin/anfragen");
    } finally {
      setLoading(false);
    }
  }, [id, navigate]);

  useEffect(() => { load(); }, [load]);

  const save = async (extra = {}) => {
    setSaving(true);
    try {
      const { data } = await api.patch(`/admin/inquiries/${id}`, { ...form, ...extra });
      setInq(data);
      if (extra.add_note) setNote("");
      toast.success("Gespeichert.");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  const doArchive = async () => { await api.post(`/admin/inquiries/${id}/${inq.archived ? "restore" : "archive"}`); toast.success(inq.archived ? "Wiederhergestellt." : "Archiviert."); load(); };
  const doDelete = async () => {
    if (!window.confirm("Anfrage endgültig löschen?")) return;
    await api.delete(`/admin/inquiries/${id}`);
    toast.success("Gelöscht.");
    navigate("/admin/anfragen");
  };

  const [resending, setResending] = useState("");
  const resend = async (target) => {
    setResending(target);
    try {
      const { data } = await api.post(`/admin/inquiries/${id}/resend-email?target=${target}`);
      setInq(data.inquiry);
      toast[data.ok ? "success" : "error"](
        data.ok
          ? (target === "admin" ? "Interne Benachrichtigung gesendet." : "Eingangsbestätigung an Kunde gesendet.")
          : "E-Mail konnte nicht gesendet werden – Details siehe Status.");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setResending("");
    }
  };

  if (loading || !inq) return <div className="flex justify-center py-20"><Loader2 className="h-8 w-8 animate-spin text-yellow" /></div>;

  const wa = inq.phone ? `https://wa.me/${inq.phone.replace(/[^0-9]/g, "").replace(/^0/, "49")}` : null;

  return (
    <div data-testid="admin-inquiry-detail">
      <div className="flex flex-wrap items-center justify-between gap-3 print:hidden">
        <Link to="/admin/anfragen" className="inline-flex items-center gap-2 font-heading text-sm font-bold text-anthracite hover:text-yellow-dark">
          <ArrowLeft className="h-4 w-4" /> Zurück
        </Link>
        <div className="flex flex-wrap gap-2">
          <button onClick={() => window.print()} className="inline-flex items-center gap-2 rounded-sm border border-light-gray px-3 py-2 text-sm font-semibold text-anthracite hover:bg-light-gray" data-testid="inq-print"><Printer className="h-4 w-4" /> Drucken / PDF</button>
          <button onClick={doArchive} className="inline-flex items-center gap-2 rounded-sm border border-light-gray px-3 py-2 text-sm font-semibold text-anthracite hover:bg-light-gray" data-testid="inq-archive">
            {inq.archived ? <><RotateCcw className="h-4 w-4" /> Wiederherstellen</> : <><Archive className="h-4 w-4" /> Archivieren</>}
          </button>
          <button onClick={doDelete} className="inline-flex items-center gap-2 rounded-sm border border-red-300 px-3 py-2 text-sm font-semibold text-red-600 hover:bg-red-50" data-testid="inq-delete"><Trash2 className="h-4 w-4" /> Löschen</button>
        </div>
      </div>

      <div className="mt-5 flex flex-wrap items-center gap-3">
        <h1 className="font-heading text-3xl font-bold text-anthracite">{inq.name}</h1>
        <span className="rounded-sm bg-dark-gray px-2.5 py-1 text-xs font-bold text-white">{inq.number}</span>
        <span className="rounded-sm bg-light-gray px-2.5 py-1 text-xs font-semibold text-mid-gray">{inq.kind === "business" ? "Geschäftskunde" : "Privatkunde"}</span>
      </div>

      {/* Contact quick actions */}
      <div className="mt-4 flex flex-wrap gap-2 print:hidden">
        {inq.phone && <a href={`tel:${inq.phone}`} className="inline-flex items-center gap-2 rounded-sm bg-anthracite px-4 py-2.5 text-sm font-bold text-white"><Phone className="h-4 w-4 text-yellow" /> {inq.phone}</a>}
        {inq.email && <a href={`mailto:${inq.email}`} className="inline-flex items-center gap-2 rounded-sm bg-anthracite px-4 py-2.5 text-sm font-bold text-white"><Mail className="h-4 w-4 text-yellow" /> {inq.email}</a>}
        {wa && <a href={wa} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-2 rounded-sm bg-anthracite px-4 py-2.5 text-sm font-bold text-white"><MessageCircle className="h-4 w-4 text-yellow" /> WhatsApp</a>}
      </div>

      <div className="mt-8 grid gap-6 lg:grid-cols-[1.4fr_1fr]">
        {/* left: data */}
        <div className="space-y-6">
          <div className="rounded-md border border-light-gray bg-white p-6">
            <h2 className="font-heading text-lg font-bold text-anthracite">Angaben</h2>
            <dl className="mt-4 grid grid-cols-2 gap-4">
              <Field label="Leistung" value={inq.service} />
              <Field label="Objektart" value={inq.object_type} />
              <Field label="Einsatzort" value={inq.location} />
              <Field label="PLZ" value={inq.postal_code} />
              <Field label="Region" value={inq.region} />
              <Field label="Fläche" value={inq.area} />
              <Field label="Etage" value={inq.floor} />
              <Field label="Aufzug" value={inq.elevator} />
              <Field label="Zeitraum" value={inq.timeframe} />
              <Field label="Rückrufzeit" value={inq.callback_time} />
              <Field label="Unternehmen" value={inq.company} />
              <Field label="Außerhalb Region" value={inq.outside_region ? "Ja" : "Nein"} />
            </dl>
            <div className="mt-4">
              <dt className="text-xs font-semibold uppercase tracking-widest text-mid-gray">Nachricht</dt>
              <dd className="mt-1 whitespace-pre-wrap text-sm text-anthracite">{inq.message}</dd>
            </div>
          </div>

          {/* files */}
          <div className="rounded-md border border-light-gray bg-white p-6">
            <h2 className="font-heading text-lg font-bold text-anthracite">Bilder &amp; Dateien ({inq.files?.length || 0})</h2>
            {inq.files?.length ? (
              <div className="mt-4 grid grid-cols-2 gap-3 sm:grid-cols-3">
                {inq.files.map((f) => (
                  f.mime_type === "application/pdf" ? (
                    <a key={f.id} href={`${API}/admin/uploads/${f.id}`} target="_blank" rel="noopener noreferrer" className="flex aspect-square flex-col items-center justify-center gap-2 rounded-sm border border-light-gray bg-light-gray p-3 text-center" data-testid={`file-${f.id}`}>
                      <FileText className="h-8 w-8 text-mid-gray" />
                      <span className="line-clamp-2 text-xs text-mid-gray">{f.filename}</span>
                    </a>
                  ) : (
                    <a key={f.id} href={`${API}/admin/uploads/${f.id}`} target="_blank" rel="noopener noreferrer" className="overflow-hidden rounded-sm border border-light-gray" data-testid={`file-${f.id}`}>
                      <img src={`${API}/admin/uploads/${f.id}`} alt={f.filename} className="aspect-square w-full object-cover" />
                    </a>
                  )
                ))}
              </div>
            ) : <p className="mt-3 text-sm text-mid-gray">Keine Dateien angehängt.</p>}
          </div>

          {/* E-Mail-Zustellungsstatus */}
          <div className="rounded-md border border-light-gray bg-white p-6" data-testid="inq-email-status">
            <h2 className="font-heading text-lg font-bold text-anthracite">E-Mail-Zustellung</h2>
            <EmailStatusRow
              label="Interne Benachrichtigung"
              recipient={"ruempeltaxi@gmail.com"}
              status={inq.email_admin_status}
              sentAt={inq.email_admin_sent_at}
              error={inq.email_admin_error}
              attempts={inq.email_admin_attempts}
              busy={resending === "admin"}
              onResend={() => resend("admin")}
              testid="email-admin"
            />
            <EmailStatusRow
              label="Eingangsbestätigung an Kunde"
              recipient={inq.email || "—"}
              status={inq.email_customer_status}
              sentAt={inq.email_customer_sent_at}
              error={inq.email_customer_error}
              attempts={inq.email_customer_attempts}
              busy={resending === "customer"}
              onResend={() => resend("customer")}
              disabled={!inq.email}
              testid="email-customer"
            />
          </div>
        </div>

        {/* right: management */}
        <div className="space-y-6 print:hidden">
          <div className="rounded-md border border-light-gray bg-white p-6">
            <h2 className="font-heading text-lg font-bold text-anthracite">Bearbeitung</h2>
            <div className="mt-4 space-y-3">
              <label className="block text-sm font-semibold text-anthracite">Status
                <select value={form.status} onChange={(e) => setForm((f) => ({ ...f, status: e.target.value }))} className="mt-1 w-full rounded-sm border border-light-gray bg-light-gray px-3 py-2.5 text-sm outline-none focus:border-yellow" data-testid="detail-status">
                  {STATUS_OPTIONS.map((s) => <option key={s} value={s}>{s}</option>)}
                </select>
              </label>
              <label className="block text-sm font-semibold text-anthracite">Zuständig
                <input value={form.assigned_to} onChange={(e) => setForm((f) => ({ ...f, assigned_to: e.target.value }))} className="mt-1 w-full rounded-sm border border-light-gray bg-light-gray px-3 py-2.5 text-sm outline-none focus:border-yellow" data-testid="detail-assigned" />
              </label>
              <div className="grid grid-cols-2 gap-3">
                <label className="block text-sm font-semibold text-anthracite">Rückruf
                  <input type="date" value={form.callback_date} onChange={(e) => setForm((f) => ({ ...f, callback_date: e.target.value }))} className="mt-1 w-full rounded-sm border border-light-gray bg-light-gray px-3 py-2.5 text-sm outline-none focus:border-yellow" data-testid="detail-callback" />
                </label>
                <label className="block text-sm font-semibold text-anthracite">Besichtigung
                  <input type="date" value={form.viewing_date} onChange={(e) => setForm((f) => ({ ...f, viewing_date: e.target.value }))} className="mt-1 w-full rounded-sm border border-light-gray bg-light-gray px-3 py-2.5 text-sm outline-none focus:border-yellow" data-testid="detail-viewing" />
                </label>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <label className="block text-sm font-semibold text-anthracite">Angebot
                  <select value={form.offer_status} onChange={(e) => setForm((f) => ({ ...f, offer_status: e.target.value }))} className="mt-1 w-full rounded-sm border border-light-gray bg-light-gray px-3 py-2.5 text-sm outline-none focus:border-yellow">
                    {OFFER_STATUS.map((s) => <option key={s} value={s}>{s || "—"}</option>)}
                  </select>
                </label>
                <label className="block text-sm font-semibold text-anthracite">Auftrag
                  <select value={form.order_status} onChange={(e) => setForm((f) => ({ ...f, order_status: e.target.value }))} className="mt-1 w-full rounded-sm border border-light-gray bg-light-gray px-3 py-2.5 text-sm outline-none focus:border-yellow">
                    {ORDER_STATUS.map((s) => <option key={s} value={s}>{s || "—"}</option>)}
                  </select>
                </label>
              </div>
              <button onClick={() => save()} disabled={saving} className="inline-flex w-full items-center justify-center gap-2 rounded-sm bg-yellow px-4 py-3 font-heading font-bold text-brand-black hover:bg-yellow-dark disabled:opacity-60" data-testid="detail-save">
                {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />} Speichern
              </button>
            </div>
          </div>

          <div className="rounded-md border border-light-gray bg-white p-6">
            <h2 className="font-heading text-lg font-bold text-anthracite">Interne Notizen</h2>
            <p className="mt-1 text-xs text-mid-gray">Nur intern sichtbar – nicht für Kunden.</p>
            <div className="mt-4 space-y-2">
              {(inq.notes || []).map((n, idx) => (
                <div key={idx} className="rounded-sm bg-light-gray p-3 text-sm">
                  <p className="text-anthracite">{n.text}</p>
                  <p className="mt-1 text-xs text-mid-gray">{n.author} · {new Date(n.at).toLocaleString("de-DE")}</p>
                </div>
              ))}
              {(!inq.notes || inq.notes.length === 0) && <p className="text-sm text-mid-gray">Noch keine Notizen.</p>}
            </div>
            <textarea value={note} onChange={(e) => setNote(e.target.value)} placeholder="Notiz hinzufügen …" className="mt-3 w-full rounded-sm border border-light-gray bg-light-gray px-3 py-2.5 text-sm outline-none focus:border-yellow" rows={3} data-testid="detail-note-input" />
            <button onClick={() => note.trim() && save({ add_note: note })} disabled={!note.trim() || saving} className="mt-2 inline-flex items-center gap-2 rounded-sm border border-anthracite px-4 py-2.5 text-sm font-bold text-anthracite hover:bg-light-gray disabled:opacity-50" data-testid="detail-note-add">
              Notiz speichern
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
